import { CometChat } from '@cometchat-pro/chat'
import { logDebug, logInfo, logWarn, logError } from '@/utils/secureLogger'

export interface CometChatConfig {
  appId: string
  region: string
  authKey: string
}

let isInitialized = false
let initializationPromise: Promise<void> | null = null
let isLoginInProgress = false
let loginPromise: Promise<CometChat.User> | null = null
const registeredMessageListeners = new Set<string>()
const registeredUserListeners = new Set<string>()

function isInitializationError(err: unknown): boolean {
  const e = err as { code?: string; name?: string; message?: string }
  const message = (e?.message || '').toLowerCase()
  return (
    e?.code === '-1' ||
    e?.name === 'COMETCHAT_INITIALIZATION_NOT_DONE' ||
    message.includes('initialize the cometchat') ||
    message.includes('initialization') ||
    message.includes('initialize')
  )
}

async function resetAndReinitialize(appId: string, region: string, authKey: string): Promise<void> {
  logWarn('Resetting CometChat initialization state and reinitializing')
  isInitialized = false
  initializationPromise = null
  await initializeCometChat({ appId, region, authKey })
  await new Promise(resolve => setTimeout(resolve, 200))
}

export async function initializeCometChat(config: CometChatConfig): Promise<void> {
  // If already initialized, return immediately
  if (isInitialized) return
  
  // If initialization is in progress, wait for it to complete
  if (initializationPromise) {
    return initializationPromise
  }
  
  // Start initialization
  initializationPromise = (async () => {
    try {
      const appSetting = new CometChat.AppSettingsBuilder()
        .subscribePresenceForAllUsers()
        .setRegion(config.region)
        .build()

      await CometChat.init(config.appId, appSetting)
      isInitialized = true
      logInfo('CometChat initialized successfully')
      
      // Small delay to ensure SDK is fully ready
      await new Promise(resolve => setTimeout(resolve, 200))
    } catch (error) {
      isInitialized = false // Ensure flag is reset on error
      initializationPromise = null // Reset on error so it can be retried
      logWarn('CometChat initialization failed', error)
      throw error
    }
  })()
  
  return initializationPromise
}

export async function loginCometChat(uid: string, authKey: string): Promise<CometChat.User> {
  // If login is already in progress, wait for it to complete
  if (isLoginInProgress && loginPromise) {
    logDebug('Login already in progress, waiting for completion', { uid: uid.substring(0, 8) + '***' })
    return loginPromise
  }
  
  // Ensure CometChat is initialized before attempting login
  if (initializationPromise) {
    await initializationPromise
  }
  
  if (!isInitialized) {
    throw new Error('CometChat must be initialized before login')
  }
  
  // Set login in progress flag
  isLoginInProgress = true
  
  // Create login promise
  loginPromise = (async () => {
    try {
      // Give the SDK a brief moment after init completion before login to avoid races
      await new Promise(resolve => setTimeout(resolve, 100))

      const user = await CometChat.login(uid, authKey)
      logDebug('CometChat login completed successfully', { uid: uid.substring(0, 8) + '***' })
      return user
    } finally {
      // Always reset login state when done
      isLoginInProgress = false
      loginPromise = null
    }
  })()
  
  return loginPromise
}

export async function createOrUpdateUser(uid: string, name: string, avatar?: string): Promise<CometChat.User> {
  try {
    // Validate input parameters
    if (!uid || uid.trim() === '') {
      throw new Error('UID is required and cannot be empty')
    }
    if (!name || name.trim() === '') {
      throw new Error('Name is required and cannot be empty')
    }
    
    // Sanitize inputs - CometChat UIDs can be alphanumeric with underscores and hyphens
    // Firebase UIDs are already valid for CometChat, so we don't need to sanitize them
    const sanitizedUid = uid.trim()
    const sanitizedName = name.trim()
    
    // Validate UID format (CometChat allows alphanumeric, underscores, and hyphens)
    const validUidPattern = /^[a-zA-Z0-9_-]+$/
    if (!validUidPattern.test(sanitizedUid)) {
      logWarn('UID contains invalid characters for CometChat', { 
        originalUid: uid.substring(0, 8) + '***',
        validPattern: 'alphanumeric, underscores, and hyphens only'
      })
      // For Firebase UIDs that might have invalid characters, we'll still try to use them
      // as CometChat might accept them in practice
    }
    
    if (sanitizedUid.length === 0) {
      throw new Error('UID cannot be empty')
    }
    
    // First try to get the user if they already exist
    try {
      const existingUser = await CometChat.getUser(sanitizedUid)
      logDebug('User already exists in CometChat', { uid: sanitizedUid.substring(0, 8) + '***' })
      return existingUser
    } catch (getUserError: unknown) {
      // User doesn't exist, proceed to create
      const err = getUserError as { code?: string }
      if (err?.code === 'ERR_UID_NOT_FOUND') {
        const user = new CometChat.User(sanitizedUid)
        user.setName(sanitizedName)
        if (avatar && avatar.trim() !== '') {
          user.setAvatar(avatar.trim())
        }
        
        logInfo('Creating new CometChat user', { 
          uid: sanitizedUid.substring(0, 8) + '***', 
          name: sanitizedName.substring(0, 10) + '***',
          hasAvatar: !!(avatar && avatar.trim() !== ''),
          nameLength: sanitizedName.length,
          uidLength: sanitizedUid.length
        })
        
        return await CometChat.createUser(user, getAuthKey())
      }
      throw getUserError
    }
  } catch (error: unknown) {
    // If user creation fails with "already exists", try to get them again
    const err = error as { code?: string, message?: string }
    if (err?.code === 'ERR_UID_ALREADY_EXISTS') {
      logDebug('User was created by another process, fetching existing user', { uid: uid.substring(0, 8) + '***' })
      try {
        return await CometChat.getUser(uid)
      } catch (getError: unknown) {
        logWarn('Failed to fetch user after creation conflict', getError)
        throw error // Throw original creation error
      }
    }
    
    // Log detailed error information for debugging
    logError('Failed to create or update CometChat user', {
      uid: uid?.substring(0, 8) + '***',
      name: name?.substring(0, 10) + '***',
      errorCode: err?.code,
      errorMessage: err?.message,
      hasAvatar: !!(avatar && avatar.trim() !== ''),
      uidLength: uid?.length,
      nameLength: name?.length,
      uidValid: /^[a-zA-Z0-9_-]+$/.test(uid?.trim() || '')
    })
    
    throw error
  }
}

export async function getLoggedInUser(): Promise<CometChat.User | null> {
  // Wait for initialization to complete if it's in progress
  if (initializationPromise) {
    await initializationPromise
  }
  
  // Check if initialized
  if (!isInitialized) {
    logWarn('getLoggedInUser called before CometChat initialization')
    return null
  }
  
  return CometChat.getLoggedinUser()
}

export async function sendTextMessage(receiverUid: string, text: string): Promise<CometChat.BaseMessage> {
  // Validate message length
  if (text.length > 10000) {
    throw new Error('Message exceeds 10,000 character limit. Please shorten your message.')
  }
  
  // Prevent users from messaging themselves
  const currentUser = await getLoggedInUser()
  if (currentUser && currentUser.getUid() === receiverUid) {
    throw new Error('You cannot send messages to yourself.')
  }
  
  // Ensure the receiver exists in CometChat
  await ensurePeerUserExists(receiverUid)
  
  const message = new CometChat.TextMessage(receiverUid, text, CometChat.RECEIVER_TYPE.USER)
  return CometChat.sendMessage(message)
}

/**
 * Send typing indicator to a specific user
 */
export async function startTypingIndicator(receiverUid: string): Promise<void> {
  const typingIndicator = new CometChat.TypingIndicator(receiverUid, CometChat.RECEIVER_TYPE.USER)
  await CometChat.startTyping(typingIndicator)
  logDebug('Typing indicator started', { receiverUid: receiverUid.substring(0, 8) + '***' })
}

/**
 * Stop typing indicator to a specific user
 */
export async function endTypingIndicator(receiverUid: string): Promise<void> {
  const typingIndicator = new CometChat.TypingIndicator(receiverUid, CometChat.RECEIVER_TYPE.USER)
  await CometChat.endTyping(typingIndicator)
  logDebug('Typing indicator stopped', { receiverUid: receiverUid.substring(0, 8) + '***' })
}

export function addMessageListener(listenerId: string, onMessage: (msg: CometChat.BaseMessage) => void): void {
  CometChat.addMessageListener(
    listenerId,
    new CometChat.MessageListener({
      onTextMessageReceived: (textMessage: CometChat.TextMessage) => {
        onMessage(textMessage)
      },
      onMediaMessageReceived: (mediaMessage: CometChat.MediaMessage) => {
        onMessage(mediaMessage)
      },
      onCustomMessageReceived: (customMessage: CometChat.CustomMessage) => {
        onMessage(customMessage)
      },
    })
  )
  registeredMessageListeners.add(listenerId)
}

export function removeMessageListener(listenerId: string): void {
  CometChat.removeMessageListener(listenerId)
  registeredMessageListeners.delete(listenerId)
}

/**
 * Fetch unread message counts for all users.
 * Returns a map of uid -> unread count.
 */
export async function fetchUnreadMessageCountMap(): Promise<Record<string, number>> {
  try {
    const response = await CometChat.getUnreadMessageCountForAllUsers(true)
    const countMap = Object.entries(response || {}).reduce<Record<string, number>>((acc, [uid, details]) => {
      const raw = details as any
      const unread =
        typeof raw === 'number'
          ? raw
          : typeof raw?.unread === 'number'
            ? raw.unread
            : typeof raw?.count === 'number'
              ? raw.count
              : 0
      if (unread > 0) {
        acc[uid] = unread
      }
      return acc
    }, {})

    logInfo('Fetched unread message counts', {
      usersWithUnread: Object.keys(countMap).length
    })

    return countMap
  } catch (error) {
    logError('Failed to fetch unread message counts', { error })
    throw error
  }
}

/**
 * Subscribe to incoming messages to increment unread counts.
 * Returns an unsubscribe function.
 */
export function subscribeToUnreadMessages(
  listenerId: string,
  currentUid: string,
  onUnreadForSender: (senderUid: string) => void
): () => void {
  addMessageListener(listenerId, (message: CometChat.BaseMessage) => {
    try {
      if (message.getReceiverType && message.getReceiverType() === CometChat.RECEIVER_TYPE.USER) {
        const receiver = (message as any).getReceiver?.()
        const receiverId = typeof receiver?.getUid === 'function' ? receiver.getUid() : receiver
        if (receiverId === currentUid) {
          const sender = (message as any).getSender?.()
          const senderId = typeof sender?.getUid === 'function' ? sender.getUid() : sender
          if (senderId) {
            onUnreadForSender(senderId)
          }
        }
      }
    } catch (error) {
      logWarn('Failed to process incoming message for unread counter', { error })
    }
  })

  return () => {
    removeMessageListener(listenerId)
    logDebug('Removed unread message listener', { listenerId })
  }
}

/**
 * Mark a message as read for a user conversation.
 */
export async function markMessageAsRead(messageId: string | number, senderUid: string): Promise<void> {
  try {
    const currentUser = await getLoggedInUser()
    if (!currentUser) {
      logWarn('Cannot mark message as read - no logged in user')
      return
    }
    await CometChat.markAsRead(messageId, senderUid, CometChat.RECEIVER_TYPE.USER, currentUser.getUid())
    logDebug('Message marked as read', {
      messageId,
      senderUid: senderUid.substring(0, 8) + '***'
    })
  } catch (error) {
    logError('Error marking message as read', error as Error)
  }
}

/**
 * Mark an entire conversation as read (all messages from a user).
 */
export async function markConversationAsReadInCometChat(senderUid: string): Promise<void> {
  try {
    const currentUser = await getLoggedInUser()
    if (!currentUser) {
      logWarn('Cannot mark conversation as read - no logged in user')
      return
    }
    // For marking a conversation as read, we use the sender's UID as both messageId and receiverId
    // and pass the current user's UID as the sender parameter
    await CometChat.markAsRead(senderUid, senderUid, CometChat.RECEIVER_TYPE.USER, currentUser.getUid())
    logDebug('Conversation marked as read in CometChat', {
      senderUid: senderUid.substring(0, 8) + '***'
    })
  } catch (error) {
    logError('Error marking conversation as read', error as Error)
  }
}

export function addUserListener(listenerId: string, onUserOnline: (user: CometChat.User) => void, onUserOffline: (user: CometChat.User) => void): void {
  CometChat.addUserListener(
    listenerId,
    new CometChat.UserListener({
      onUserOnline: (onlineUser: CometChat.User) => {
        onUserOnline(onlineUser)
      },
      onUserOffline: (offlineUser: CometChat.User) => {
        onUserOffline(offlineUser)
      }
    })
  )
  registeredUserListeners.add(listenerId)
}

export function removeUserListener(listenerId: string): void {
  CometChat.removeUserListener(listenerId)
  registeredUserListeners.delete(listenerId)
}

export async function getUserStatus(uid: string): Promise<string> {
  try {
    const user = await CometChat.getUser(uid)
    const status = user.getStatus()
    logDebug('User status retrieved', { uid: uid.substring(0, 8) + '***', status })
    return status
  } catch (error) {
    logDebug('Failed to get user status, defaulting to offline', { 
      uid: uid.substring(0, 8) + '***', 
      error: (error as any)?.code || 'UNKNOWN'
    })
    return 'offline'
  }
}

/**
 * Batch get user statuses for multiple users (more efficient than individual calls)
 */
export async function getBatchUserStatuses(uids: string[]): Promise<Map<string, boolean>> {
  const statusMap = new Map<string, boolean>()
  
  try {
    // Use Promise.all for parallel requests
    const userPromises = uids.map(async (uid) => {
      try {
        const user = await CometChat.getUser(uid)
        return {
          uid,
          isOnline: user.getStatus() === CometChat.USER_STATUS.ONLINE
        }
      } catch (error) {
        logDebug('Failed to get user status in batch', { 
          uid: uid.substring(0, 8) + '***', 
          error: (error as any)?.code || 'UNKNOWN'
        })
        return {
          uid,
          isOnline: false
        }
      }
    })

    const results = await Promise.all(userPromises)
    
    results.forEach(({ uid, isOnline }) => {
      statusMap.set(uid, isOnline)
    })

    logInfo('Batch user statuses retrieved', { 
      totalUsers: uids.length,
      onlineUsers: results.filter(r => r.isOnline).length
    })

  } catch (error) {
    logError('Error in batch user status retrieval', error)
    // Return map with all users offline as fallback
    uids.forEach(uid => statusMap.set(uid, false))
  }

  return statusMap
}

export function isUserOnline(status: string): boolean {
  // CometChat status can be 'online', 'offline', or other values
  return status === 'online' || status === CometChat.USER_STATUS.ONLINE
}

function getAuthKey(): string {
  const key = import.meta.env.VITE_COMETCHAT_AUTH_KEY
  if (!key) throw new Error('VITE_COMETCHAT_AUTH_KEY is not set')
  return key
}

export async function ensureInitializedAndLoggedIn(uid: string, name?: string): Promise<void> {
  const appId = import.meta.env.VITE_COMETCHAT_APP_ID
  const region = import.meta.env.VITE_COMETCHAT_REGION
  const authKey = getAuthKey()
  if (!appId || !region) throw new Error('CometChat env vars (APP_ID/REGION) are missing')

  logInfo('Ensuring CometChat is initialized and logged in', { 
    uid: uid.substring(0, 8) + "***",
    appId: appId.substring(0, 8) + "***",
    region 
  })

  // Ensure initialization is complete before proceeding
  try {
    await initializeCometChat({ appId, region, authKey })
    logDebug('CometChat initialization confirmed', { isInitialized })
  } catch (initError: unknown) {
    const err = initError as { message?: string; code?: string }
    logWarn('Failed to initialize CometChat', {
      error: err?.message,
      code: err?.code
    })
    throw new Error(`CometChat initialization failed: ${err?.message || 'Unknown error'}`)
  }
  
  try {
    let current = null;
    
    // Try to get logged in user, but handle case where CometChat isn't ready yet
    try {
      current = await getLoggedInUser();
    } catch (getUserError: unknown) {
      // If CometChat says it's not initialized, wait a bit longer and retry once
      if (isInitializationError(getUserError)) {
        logDebug('CometChat not ready yet, waiting and retrying...', { 
          code: (getUserError as any)?.code,
          isInitialized 
        })
        await new Promise(resolve => setTimeout(resolve, 300))
        try {
          current = await getLoggedInUser();
        } catch (retryError) {
          logDebug('Retry failed, proceeding with login attempt', retryError)
          current = null; // Proceed to login
        }
      } else {
        logWarn('Unexpected error getting logged in user', getUserError)
        current = null; // Proceed to login
      }
    }
    
    if (!current || current.getUid() !== uid) {
      // User not logged in or different user, need to login
      try {
        // Try to login first
        logInfo('Attempting CometChat login', { uid: uid.substring(0, 8) + "***" })
        await loginCometChat(uid, authKey)
        logInfo('Successfully logged into CometChat', { uid: uid.substring(0, 8) + "***" })
      } catch (loginError: unknown) {
        // If login fails, try to create user then login
        const error = loginError as { code?: string; name?: string; message?: string }

        // If initialization error, wait and retry once
        if (isInitializationError(error)) {
          logDebug('Login failed due to initialization, retrying after delay...', {
            code: error?.code,
            isInitialized
          })
          await new Promise(resolve => setTimeout(resolve, 300))
          
          try {
            await loginCometChat(uid, authKey)
            logInfo('Successfully logged into CometChat after retry', { uid: uid.substring(0, 8) + "***" })
          } catch (retryError: unknown) {
            const retryErr = retryError as { code?: string; message?: string }
            if (retryErr?.code === 'ERR_UID_NOT_FOUND') {
              logDebug('User not found on retry, creating...', { uid: uid.substring(0, 8) + "***" })
              await createOrUpdateUser(uid, name || uid)
              await loginCometChat(uid, authKey)
              logInfo('Created and logged into CometChat', { uid: uid.substring(0, 8) + "***" })
            } else if (isInitializationError(retryError)) {
              logWarn('Initialization still failing during login retry, forcing reinitialize', {
                code: (retryError as any)?.code,
                name: (retryError as any)?.name
              })
              await resetAndReinitialize(appId, region, authKey)
              await loginCometChat(uid, authKey)
              logInfo('Successfully logged into CometChat after forced reinitialize', {
                uid: uid.substring(0, 8) + "***"
              })
            } else {
              throw retryError
            }
          }
        } else if (error?.code === 'ERR_UID_NOT_FOUND') {
          logDebug('User not found, creating new CometChat user', { uid: uid.substring(0, 8) + "***" })
          await createOrUpdateUser(uid, name || uid)
          await loginCometChat(uid, authKey)
          logInfo('Created and logged into CometChat', { uid: uid.substring(0, 8) + "***" })
        } else {
          logWarn('CometChat login failed with error', {
            code: error?.code,
            name: (error as any)?.name,
            message: error?.message,
            uid: uid.substring(0, 8) + "***"
          })
          throw loginError
        }
      }
    } else {
      logDebug('Already logged into CometChat', { uid: uid.substring(0, 8) + "***" })
    }
  } catch (error: unknown) {
    const err = error as { message?: string; code?: string }
    logWarn('Failed to ensure CometChat login', {
      error: err?.message,
      code: err?.code,
      uid: uid.substring(0, 8) + "***"
    })
    throw error
  }
}

export async function fetchUserMessages(peerUid: string, limit = 50): Promise<CometChat.BaseMessage[]> {
  try {
    // Ensure the current user is logged in before fetching messages
    const loggedIn = await getLoggedInUser()
    if (!loggedIn) {
      logWarn('User not logged in when attempting to fetch messages', { 
        peerUid: peerUid.substring(0, 8) + '***',
        hasInitializationPromise: !!initializationPromise,
        isInitialized 
      })
      
      // Give the SDK a brief moment to complete login state propagation
      await new Promise(resolve => setTimeout(resolve, 300))
      
      // Retry once after delay
      const retryLoggedIn = await getLoggedInUser()
      if (!retryLoggedIn) {
        throw { 
          code: 'USER_NOT_LOGGED_IN', 
          name: 'User not logged-in', 
          message: 'Login required before fetching messages - user still not logged in after retry' 
        }
      }
      logInfo('Login state recovered after delay', {
        peerUid: peerUid.substring(0, 8) + '***'
      })
    }

    // First, ensure the peer user exists in CometChat
    await ensurePeerUserExists(peerUid)
    
    const request = new CometChat.MessagesRequestBuilder()
      .setUID(peerUid)
      .setLimit(limit)
      .build()
    
    logDebug('Fetching messages from CometChat', {
      peerUid: peerUid.substring(0, 8) + '***',
      limit
    })
    
    const messages = await request.fetchPrevious()
    logDebug(`Fetched ${messages.length} messages successfully`, {
      peerUid: peerUid.substring(0, 8) + '***',
      count: messages.length
    })
    
    return messages
  } catch (error: unknown) {
    const err = error as { code?: string; message?: string }
    logWarn('Error fetching user messages', {
      peerUid: peerUid.substring(0, 8) + '***',
      errorCode: err?.code,
      errorMessage: err?.message,
      isInitialized,
      isLoginInProgress
    })
    
    if (err?.code === 'ERR_UID_NOT_FOUND') {
      logDebug('User not found in CometChat, returning empty messages', { peerUid })
      return []
    }
    throw error
  }
}

export async function ensurePeerUserExists(peerUid: string): Promise<void> {
  try {
    // Try to get the user first
    await CometChat.getUser(peerUid)
    logDebug('Peer user exists in CometChat', { peerUid: peerUid.substring(0, 8) + '***' })
  } catch (error: unknown) {
    const err = error as { code?: string }
    if (err?.code === 'ERR_UID_NOT_FOUND') {
      // User doesn't exist, create a placeholder user
      logDebug('Creating placeholder user for peer', { peerUid: peerUid.substring(0, 8) + '***' })
      try {
        await createOrUpdateUser(peerUid, `User ${peerUid.substring(0, 8)}`)
        logInfo('Successfully created placeholder user', { peerUid: peerUid.substring(0, 8) + '***' })
      } catch (createError) {
        logWarn('Failed to create placeholder user - messaging may be limited until user logs in', {
          peerUid: peerUid.substring(0, 8) + '***',
          errorCode: (createError as any)?.code,
          errorMessage: (createError as any)?.message
        })
        // Don't throw here - we want messaging to continue even if user creation fails
        // The messaging system will handle missing users when they eventually log in
      }
    } else {
      logWarn('Unexpected error checking peer user existence', {
        peerUid: peerUid.substring(0, 8) + '***',
        errorCode: err?.code,
        error
      })
      // Don't throw - allow messaging to continue
    }
  }
}

/**
 * Cleanly logout from CometChat and reset local SDK state.
 * Call this during app logout to avoid stale sessions on next login.
 */
export async function logoutCometChat(): Promise<void> {
  try {
    logInfo('Logging out from CometChat and resetting SDK state')
    // Attempt SDK logout
    try {
      await CometChat.logout()
      logDebug('CometChat.logout() completed')
    } catch (sdkError) {
      logWarn('CometChat.logout() failed or user already logged out', { error: sdkError })
    }

    // Remove any registered listeners to prevent leaks and stale callbacks
    for (const id of Array.from(registeredMessageListeners)) {
      try { CometChat.removeMessageListener(id) } catch {}
    }
    for (const id of Array.from(registeredUserListeners)) {
      try { CometChat.removeUserListener(id) } catch {}
    }
    registeredMessageListeners.clear()
    registeredUserListeners.clear()

    // Reset local state flags so next init is fresh
    isInitialized = false
    initializationPromise = null
    isLoginInProgress = false
    loginPromise = null

    // Small delay to allow SDK internal teardown
    await new Promise(resolve => setTimeout(resolve, 100))
    logInfo('CometChat SDK state reset complete')
  } catch (error) {
    logWarn('Failed to fully reset CometChat state during logout', { error })
  }
}
